// The chats page's person panel inside the HRDeva browser extension's side
// panel. While that side panel is open in the tab's window, ChatsPage hides its
// own panel + toggle and publishes what the panel shows (PanelModel); the side
// panel renders PersonPanel from it in a frame of /embed/person-panel and sends
// clicks back as PanelActions, which this tab performs.
//
//   page ⇄ extension content script — DOM CustomEvents on `document`, detail =
//     JSON string: hrdeva-ext:hello / hrdeva-ext:panel / hrdeva-ext:action in,
//     hrdeva-ats:model out;
//   side panel ⇄ /embed/person-panel frame — window.postMessage.
// The page never dispatches hrdeva-ext:* itself: a hello tells the extension's
// content script that a newer copy of it took the document over.
//
// Contract v1. The extension keeps a copy of these types and parsers in
// apps/hh-resume-extension/src/shared/ats-panel.ts — change both together.
// Without the extension nobody sends the events: the page behaves as before.

import { useEffect, useRef, useSyncExternalStore } from "react";
import type { ChatPersonThreads, ChatThreadMessages } from "@/api/client";

export interface PanelModel {
  v: 1;
  /** chat person key of the open chat */
  key: string;
  /** exactly api.chatPersonThreads(key); null while it loads — the panel is
   *  an empty column then, here and in the side panel */
  personThreads: ChatPersonThreads | null;
  /** first thread with a candidate, or null */
  candidate: ChatThreadMessages["candidate"];
  /** resume whose photo the panel shows: the list row's avatar resume, else
   *  the first thread with an hh resume, or null */
  hhResumeId: string | null;
  /** «Активный чат» */
  active: boolean;
}

export type PanelAction =
  | { type: "toggleActive"; key: string }
  | { type: "openedThread"; key: string; threadId: string }
  /** same-origin app path, e.g. "/candidates/<id>" */
  | { type: "navigate"; key: string; path: string };

export const EXT_HELLO_EVENT = "hrdeva-ext:hello";
export const EXT_PANEL_EVENT = "hrdeva-ext:panel";
export const EXT_ACTION_EVENT = "hrdeva-ext:action";
export const ATS_MODEL_EVENT = "hrdeva-ats:model";

/** What the person panel shows for the open chat — the page's own panel and
 *  the extension's render from the same model. Null only when no chat is open:
 *  a chat still loading is a model without threads, so switching chats doesn't
 *  flash «Выберите чат в HRDeva» in the side panel. */
export function panelModelFrom(
  key: string | null,
  personThreads: ChatPersonThreads | null,
  threadsData: ChatThreadMessages[] | null,
  active: boolean,
  avatarResumeId: string | null = null,
): PanelModel | null {
  if (!key) return null;
  return {
    v: 1,
    key,
    personThreads,
    // Candidate + resume can come from any thread (usually the hh one).
    candidate: threadsData?.find((t) => t.candidate)?.candidate ?? null,
    // The list's avatar first: it is any of the person's resumes that HAS a
    // photo, while a thread only carries the hh one — a TG/MAX-only chat has
    // none, so the list showed a face and the panel the initials.
    hhResumeId:
      avatarResumeId ?? threadsData?.find((t) => t.hh_resume_id)?.hh_resume_id ?? null,
    active,
  };
}

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isStrOrNull(v: unknown): v is string | null {
  return v === null || typeof v === "string";
}

/** An app route the page may be told to open: "/…", never "//host" or "/\host". */
export function isAppPath(path: unknown): path is string {
  return (
    typeof path === "string" &&
    path.startsWith("/") &&
    !/^\/[/\\]/.test(path) &&
    // no control characters or backslashes anywhere
    !/[\x00-\x1f\\]/.test(path)
  );
}

/** A DOM event's detail: JSON string of a v1 object, anything else → null. */
export function parseEventDetail(detail: unknown): Obj | null {
  if (typeof detail !== "string") return null;
  try {
    const data: unknown = JSON.parse(detail);
    return isObj(data) && data.v === 1 ? data : null;
  } catch {
    return null;
  }
}

/** hrdeva-ext:panel → whether the side panel is open, or null when malformed. */
export function parsePanelState(detail: unknown): boolean | null {
  const d = parseEventDetail(detail);
  return d && typeof d.open === "boolean" ? d.open : null;
}

export function parsePanelAction(raw: unknown): PanelAction | null {
  if (!isObj(raw) || typeof raw.key !== "string" || !raw.key) return null;
  const key = raw.key;
  switch (raw.type) {
    case "toggleActive":
      return { type: "toggleActive", key };
    case "openedThread":
      return typeof raw.threadId === "string" && raw.threadId
        ? { type: "openedThread", key, threadId: raw.threadId }
        : null;
    case "navigate":
      return isAppPath(raw.path) ? { type: "navigate", key, path: raw.path } : null;
    default:
      return null;
  }
}

/** Checks the fields PersonPanel reads — a malformed model must not crash the frame. */
export function parsePanelModel(raw: unknown): PanelModel | null {
  if (!isObj(raw) || raw.v !== 1 || typeof raw.key !== "string" || !raw.key) return null;
  const pt = raw.personThreads;
  if (
    pt !== null &&
    (!isObj(pt) ||
      typeof pt.name !== "string" ||
      !isStrOrNull(pt.person_id) ||
      typeof pt.has_phone !== "boolean" ||
      !Array.isArray(pt.threads) ||
      !pt.threads.every(
        (t) =>
          isObj(t) &&
          typeof t.thread_id === "string" &&
          typeof t.channel === "string" &&
          isStrOrNull(t.subtitle ?? null),
      ))
  ) {
    return null;
  }
  const c = raw.candidate;
  if (
    c !== null &&
    !(isObj(c) && typeof c.id === "string" && isStrOrNull(c.decision) && isStrOrNull(c.summary))
  ) {
    return null;
  }
  if (!isStrOrNull(raw.hhResumeId) || typeof raw.active !== "boolean") return null;
  return raw as unknown as PanelModel;
}

export function modelEventDetail(model: PanelModel | null): string {
  return JSON.stringify({ v: 1, model });
}

// ── side panel ⇄ /embed/person-panel frame ──────────────────────────────────

export const EMBED_READY_MESSAGE = { source: "hrdeva-embed", type: "panel:ready", v: 1 } as const;

export function embedActionMessage(action: PanelAction) {
  return { source: "hrdeva-embed", type: "panel:action", v: 1, action } as const;
}

/** The side panel's `panel:model` → { model }, or null for anything else. */
export function parseModelMessage(data: unknown): { model: PanelModel | null } | null {
  if (!isObj(data) || data.source !== "hrdeva-ext" || data.type !== "panel:model" || data.v !== 1) {
    return null;
  }
  if (data.model === null) return { model: null };
  const model = parsePanelModel(data.model);
  return model ? { model } : null;
}

// ── page side: one listener set for the whole app ───────────────────────────
// The content script reports the side panel state when it starts (document
// idle) and on changes — possibly while ChatsPage isn't mounted (the recruiter
// is on /vacancies). So the state lives here, listened for from module load;
// the hook only reads it. main.tsx imports this module up front for that: via
// ChatsPage alone it would load with the lazy chats chunk, too late. `hello`
// is answered even with no chats page: null clears a model the extension kept
// from before a reload.

const MODEL_DEBOUNCE_MS = 50;

interface Slot {
  model: PanelModel | null;
  onAction: (action: PanelAction) => void;
}

let extPanelOpen = false;
const openListeners = new Set<() => void>();
let owner: Slot | null = null;
let lastSent: string | null = null;

function publish(model: PanelModel | null, force = false) {
  const detail = modelEventDetail(model);
  if (!force && detail === lastSent) return;
  lastSent = detail;
  document.dispatchEvent(new CustomEvent(ATS_MODEL_EVENT, { detail }));
}

function subscribeOpen(listener: () => void) {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

// ── is the extension here: «Расширение для браузера» ────────────────────────
// The extension's content script says hello in every newats tab at document
// idle (and again after the back/forward cache or an update). No hello — not
// installed in this browser profile, or disabled. A hello without `version`
// comes from an extension older than this page: installed, version unknown.

export interface ExtensionPresence {
  installed: boolean;
  /** "1.4.120"; null when the extension didn't say */
  version: string | null;
}

const VERSION_RE = /^\d{1,9}\.\d{1,9}\.\d{1,9}$/;

export function presenceFromHello(detail: Record<string, unknown>): ExtensionPresence {
  const v = detail.version;
  return { installed: true, version: typeof v === "string" && VERSION_RE.test(v) ? v : null };
}

let presence: ExtensionPresence = { installed: false, version: null };
const presenceListeners = new Set<() => void>();

function setPresence(next: ExtensionPresence) {
  if (next.installed === presence.installed && next.version === presence.version) return;
  presence = next;
  presenceListeners.forEach((fn) => fn());
}

function subscribePresence(listener: () => void) {
  presenceListeners.add(listener);
  return () => {
    presenceListeners.delete(listener);
  };
}

/** What the extension last told this tab about itself. */
export function useExtensionPresence(): ExtensionPresence {
  return useSyncExternalStore(subscribePresence, () => presence, () => presence);
}

// Top-level tab only: the same bundle runs in the side panel's
// /embed/person-panel frame, which must never answer for the tab.
if (typeof document !== "undefined" && window.self === window.top) {
  document.addEventListener(EXT_PANEL_EVENT, (e) => {
    const open = parsePanelState((e as CustomEvent).detail);
    if (open === null || open === extPanelOpen) return;
    extPanelOpen = open;
    openListeners.forEach((fn) => fn());
  });
  document.addEventListener(EXT_HELLO_EVENT, (e) => {
    const d = parseEventDetail((e as CustomEvent).detail);
    if (!d) return;
    setPresence(presenceFromHello(d));
    publish(owner?.model ?? null, true);
  });
  document.addEventListener(EXT_ACTION_EVENT, (e) => {
    const d = parseEventDetail((e as CustomEvent).detail);
    const action = d && parsePanelAction(d.action);
    if (action && owner) owner.onAction(action);
  });
}

/**
 * Publishes `model` to the extension (debounced, only when its content
 * changed; null on unmount) and routes its actions to `onAction`. Returns
 * whether the extension side panel is open in this tab's window.
 */
export function useExtensionPanel({
  model,
  onAction,
}: {
  model: PanelModel | null;
  onAction: (action: PanelAction) => void;
}): boolean {
  const open = useSyncExternalStore(subscribeOpen, () => extPanelOpen, () => false);

  const onActionRef = useRef(onAction);
  useEffect(() => {
    onActionRef.current = onAction;
  });

  const slotRef = useRef<Slot | null>(null);
  useEffect(() => {
    const slot: Slot = { model: null, onAction: (a) => onActionRef.current(a) };
    slotRef.current = slot;
    owner = slot;
    return () => {
      slotRef.current = null;
      if (owner !== slot) return;
      owner = null;
      // The chats page is gone: the side panel falls back to its placeholder.
      publish(null);
    };
  }, []);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    slot.model = model;
    const timer = window.setTimeout(() => {
      if (owner === slot) publish(model);
    }, MODEL_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [model]);

  return open;
}
